import { useGetAgendaRecordModalQuery, useGetAgendaRecordQuery } from 'store/services/flow/agendaRecords';
import { useMemo } from 'react';

import type { GetValueFormItems } from 'types';
import type { UserItem } from 'types/formItem';

interface UseGetValuesDirectProps {
  agendaId: string;
  recordId: string;
  valueFormItems: GetValueFormItems[];
  isModal: boolean;
  skip?: boolean;
}

/* This hook is a workaround. Because the backend currently doesn't handle
 * the retrieval of values for a specific record in variable view column filtering,
 *  we need to fetch the record and then filter the values from the response.
 *
 * NOTE: We force refetch here to ensure fresh data when recordId changes,
 * which is critical for variable columns in modal contexts where the selected
 * record changes and we need the updated valueRecordIds immediately.
 */
export const useGetValuesDirect = ({ agendaId, recordId, valueFormItems, isModal, skip = false }: UseGetValuesDirectProps): GetValueFormItems[] => {
  /**
   * Modal uses separate query to avoid cache invalidation in UserModulesAgendaRecordDetail.
   * When isModal=true, refetchOnMountOrArgChange invalidates the parent's cache,
   * causing full UserAgendaTable + modal reload.
   */
  const { data } = (isModal ? useGetAgendaRecordModalQuery : useGetAgendaRecordQuery)(
    {
      agendaId,
      recordId,
    },
    {
      skip: skip || !recordId || !agendaId || recordId.startsWith('new'),
      refetchOnMountOrArgChange: isModal,
    }
  );

  return useMemo(() => {
    if (!data || !valueFormItems || valueFormItems.length === 0) return [];

    const formItems = data?.tabs
      ?.flatMap((tab) => tab.sections || [])
      ?.flatMap((section) => section.formItemTabs || [])
      ?.flatMap((formItemTab) => formItemTab.formItems || []);

    if (!formItems || formItems.length === 0) {
      return [];
    }

    return valueFormItems.map((item): GetValueFormItems => {
      if (!item || !item.valueFormItemId) {
        return {
          ...item,
          valueDirect: null,
          valueSourceType: null,
          valueFormItemId: null,
        };
      }

      const attribute = formItems.find((fi) => fi.formItem.id === item.valueFormItemId)?.attribute;
      if (!attribute) return { ...item, valueRecordIds: [recordId] };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const attributeType = Object.keys(attribute).find((key) => (attribute as any)[key] !== null && key !== 'id') ?? null;

      if (attributeType === 'recordsAttribute')
        return {
          ...item,
          valueRecordIds: attribute.recordsAttribute?.records || [],
        };

      if (attributeType === 'usersAttribute')
        return {
          ...item,
          valueDirect: (attribute.usersAttribute?.users as unknown as UserItem[]).map((user) => user.userId) || [],
          valueFormItemId: null,
        };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const valueDirect = attributeType ? (attribute as any)[attributeType]?.value : null;

      return {
        ...item,
        valueDirect: valueDirect ? [String(valueDirect)] : null,
        valueSourceType: null,
        valueFormItemId: null,
      };
    });
  }, [data, valueFormItems, recordId]);
};
